import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import {
  Car,
  MapPin,
  Clock,
  Plus,
  Receipt,
  PencilLine,
  DollarSign,
  Fuel,
  Plane,
  Handshake,
  Target,
  Percent,
  Minus,
  X,
  ArrowLeftRight,
} from 'lucide-react'
import type { BookingAdjustment } from '@limo/database'

// Types from @limo/shared/pricing
interface PricingLineItem {
  label: string
  amount: number
  type:
    | 'base'
    | 'distance'
    | 'time'
    | 'addon'
    | 'gratuity'
    | 'tax'
    | 'discount'
    | 'adjustment'
    | 'fuel'
    | 'airport'
    | 'meet_greet'
    | 'minimum'
    | 'service'
}

interface BookingCostBreakdownProps {
  pricingBreakdown: PricingLineItem[] | null
  subtotal: number
  gratuityAmount: number | null
  gratuityPercent: number | null
  taxes: number | null
  totalAmount: number
  adjustmentsTotal?: number
  paymentsTotal?: number
  onAddAdjustment?: () => void
  /** Opens the Apply Discount dialog. Omitted when discounting isn't allowed. */
  onApplyDiscount?: () => void
  /** Set instead of onApplyDiscount to show the action disabled, with the reason. */
  discountBlockedReason?: string
  /** True when a discount is already applied, so the action reads "Edit". */
  hasDiscount?: boolean
  /** Individual adjustments for displaying as line items */
  adjustments?: BookingAdjustment[]
  /** Callback when user wants to remove an adjustment */
  onRemoveAdjustment?: (adjustmentId: string) => void
  /** Whether this booking is a round trip */
  isRoundTrip?: boolean
  /**
   * True when this booking is one leg of a paired round trip
   * (`paired_booking_id IS NOT NULL`). When true, the card hides its
   * "Payments Applied / Balance Due / Credit" footer rows — those figures
   * belong to the whole pair and are rendered in the sibling Payments card
   * against the combined total. Without this, showing per-leg total and
   * per-leg payments on the same card produces phantom credits when the
   * payment was recorded as a single combined charge (legacy pre-Phase-4
   * webhook behavior) or as a per-leg split (new Phase 4 behavior).
   */
  isPairedBooking?: boolean
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
  }).format(amount)

// Map line item types to icons
const getLineItemIcon = (type: PricingLineItem['type']) => {
  switch (type) {
    case 'base':
      return Car
    case 'distance':
      return MapPin
    case 'time':
      return Clock
    case 'addon':
      return Plus
    case 'service':
      return Receipt
    case 'adjustment':
      return PencilLine
    case 'fuel':
      return Fuel
    case 'airport':
      return Plane
    case 'meet_greet':
      return Handshake
    case 'minimum':
      return Target
    case 'discount':
      return Percent
    default:
      return DollarSign
  }
}

// Helper to get icon for adjustment type
const getAdjustmentIcon = (type: string) => {
  switch (type) {
    case 'discount':
    case 'refund_credit':
      return Minus
    case 'fee':
    case 'extra_hours':
    default:
      return Plus
  }
}

// Helper to check if adjustment type is negative (reduces total)
const isNegativeAdjustmentType = (type: string) => {
  return type === 'discount' || type === 'refund_credit'
}

export function BookingCostBreakdown({
  pricingBreakdown,
  subtotal,
  gratuityAmount,
  gratuityPercent,
  taxes,
  totalAmount,
  adjustmentsTotal = 0,
  paymentsTotal = 0,
  onAddAdjustment,
  onApplyDiscount,
  discountBlockedReason,
  hasDiscount = false,
  adjustments = [],
  onRemoveAdjustment,
  isRoundTrip,
  isPairedBooking = false,
}: BookingCostBreakdownProps) {
  const breakdown = pricingBreakdown || []
  const balanceDue = totalAmount + adjustmentsTotal - paymentsTotal

  // Total of any discount line items baked into the pricing (e.g. a GHL-applied
  // quote discount). Negative in the breakdown; surfaced as a summary badge.
  const discountTotal = breakdown
    .filter((item) => item.type === 'discount')
    .reduce((sum, item) => sum + item.amount, 0)

  return (
    <Card>
      <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
        <CardTitle className="flex items-center gap-2">
          <Receipt className="h-5 w-5" />
          Cost Breakdown
          {isRoundTrip && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <ArrowLeftRight className="h-3 w-3" />
              Round Trip
            </span>
          )}
          {discountTotal < 0 && (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-700">
              <Percent className="h-3 w-3" />
              Discount −{formatCurrency(Math.abs(discountTotal))}
            </span>
          )}
        </CardTitle>
        <div className="flex items-center gap-2">
          {(onApplyDiscount || discountBlockedReason) && (
            <Button
              variant="outline"
              size="sm"
              onClick={onApplyDiscount}
              disabled={!onApplyDiscount}
              title={discountBlockedReason}
            >
              <Percent className="h-3 w-3 mr-1" />
              {hasDiscount ? 'Edit Discount' : 'Apply Discount'}
            </Button>
          )}
          {onAddAdjustment && (
            <Button variant="outline" size="sm" onClick={onAddAdjustment}>
              <Plus className="h-3 w-3 mr-1" />
              Add Adjustment
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="space-y-4">
          {/* Line items */}
          {breakdown.length > 0 ? (
            <div className="space-y-2">
              {breakdown.map((item, index) => {
                const Icon = getLineItemIcon(item.type)
                const isNegative = item.amount < 0
                return (
                  <div
                    key={`${item.type}-${index}`}
                    className="flex items-center justify-between text-sm"
                  >
                    <div className="flex items-center gap-2">
                      <Icon className="h-4 w-4 text-muted-foreground" />
                      <span>{item.label}</span>
                    </div>
                    <span className={isNegative ? 'text-green-600' : ''}>
                      {isNegative ? '-' : ''}
                      {formatCurrency(Math.abs(item.amount))}
                    </span>
                  </div>
                )
              })}
            </div>
          ) : (
            <p className="text-sm text-muted-foreground">
              No pricing breakdown available
            </p>
          )}

          {/* Subtotal */}
          <div className="flex justify-between text-sm border-t pt-2">
            <span className="text-muted-foreground">Subtotal</span>
            <span>{formatCurrency(subtotal)}</span>
          </div>

          {/* Gratuity */}
          {gratuityAmount !== null && Number(gratuityAmount) > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">
                Gratuity {gratuityPercent ? `(${gratuityPercent}%)` : ''}
              </span>
              <span>{formatCurrency(Number(gratuityAmount))}</span>
            </div>
          )}

          {/* Taxes */}
          {taxes !== null && Number(taxes) > 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground">Taxes</span>
              <span>{formatCurrency(Number(taxes))}</span>
            </div>
          )}

          {/* Original Total */}
          <div className="flex justify-between border-t pt-2 font-medium">
            <span>Original Total</span>
            <span>{formatCurrency(totalAmount)}</span>
          </div>

          {/* Individual Adjustments */}
          {adjustments.length > 0 && (
            <div className="space-y-2">
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <PencilLine className="h-3 w-3" />
                <span>Adjustments:</span>
              </div>
              {adjustments.map((adjustment) => {
                const Icon = getAdjustmentIcon(adjustment.adjustment_type)
                const isNegative = isNegativeAdjustmentType(adjustment.adjustment_type)
                const displayAmount = Number(adjustment.amount)

                return (
                  <div
                    key={adjustment.id}
                    className="flex items-center justify-between text-sm pl-4 group"
                  >
                    <div className="flex items-center gap-2 min-w-0">
                      <Icon className={`h-3 w-3 flex-shrink-0 ${isNegative ? 'text-green-600' : 'text-muted-foreground'}`} />
                      <span className="truncate">{adjustment.label}</span>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className={isNegative ? 'text-green-600' : ''}>
                        {isNegative ? '-' : '+'}
                        {formatCurrency(displayAmount)}
                      </span>
                      {onRemoveAdjustment && (
                        <button
                          type="button"
                          onClick={() => onRemoveAdjustment(adjustment.id)}
                          className="opacity-0 group-hover:opacity-100 transition-opacity p-1 hover:bg-muted rounded text-muted-foreground hover:text-destructive"
                          title="Remove adjustment"
                        >
                          <X className="h-3 w-3" />
                        </button>
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          )}

          {/* Legacy: Show adjustments total if no individual adjustments provided but total exists */}
          {adjustments.length === 0 && adjustmentsTotal !== 0 && (
            <div className="flex justify-between text-sm">
              <span className="text-muted-foreground flex items-center gap-1">
                <PencilLine className="h-3 w-3" />
                Adjustments
              </span>
              <span className={adjustmentsTotal < 0 ? 'text-green-600' : ''}>
                {adjustmentsTotal >= 0 ? '+' : '-'}
                {formatCurrency(Math.abs(adjustmentsTotal))}
              </span>
            </div>
          )}

          {/* Adjusted Total (if different from original) */}
          {adjustmentsTotal !== 0 && (
            <div className="flex justify-between font-medium">
              <span>Adjusted Total</span>
              <span>{formatCurrency(totalAmount + adjustmentsTotal)}</span>
            </div>
          )}

          {/* Payments Applied + Balance Due — hidden on paired-round-trip
              legs. For paired bookings the payment + balance figures apply
              to the whole pair and are rendered in the Payments card against
              the combined total, so showing them here too produced phantom
              credits when the per-leg total was compared against the full
              combined charge. Legacy single-row round trips (isRoundTrip
              true but isPairedBooking false) still show this section. */}
          {!isPairedBooking && paymentsTotal > 0 && (
            <div className="flex justify-between text-sm border-t pt-2">
              <span className="text-muted-foreground">Payments Applied</span>
              <span className="text-green-600">
                -{formatCurrency(paymentsTotal)}
              </span>
            </div>
          )}

          {!isPairedBooking && (paymentsTotal > 0 || adjustmentsTotal !== 0) && (
            <div
              className={`flex justify-between border-t pt-2 font-bold text-lg ${
                balanceDue > 0 ? 'text-red-600' : balanceDue < 0 ? 'text-green-600' : ''
              }`}
            >
              <span>{balanceDue < 0 ? 'Credit' : 'Balance Due'}</span>
              <span>{formatCurrency(Math.abs(balanceDue))}</span>
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  )
}
